import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Link } from 'react-router-dom'; // Use react-router-dom Link
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'nav_home', href: '#home', link: '/' },
    { key: 'nav_programme', href: '#programme', link: '/programme' },
    { key: 'nav_services', href: '#services', link: '/services' },
    { key: 'nav_facilities', href: '#facilities', link: '/facilities' },
    { key: 'nav_doctors', href: '#doctors', link: '/expert' },
    { key: 'nav_procedures', href: '#procedures', link: '/procedures' },
    { key: 'nav_contact', href: '#contact', link: '/contact' },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="main-header"
      className={`bg-white fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="logo-link"
            onClick={() => scrollToSection('#home')}
          >
            <div className="w-10 h-10 flex items-center justify-center">
              <img
                src="/logo.png"
                alt="GastroCare Logo"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <div className="hidden sm:block">
              <p className="font-bold text-[#800000] text-lg leading-tight">
                GastroCare
              </p>
              <p className="text-xs text-slate-500">Obesity Cure</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.link}
                className="text-slate-600 hover:text-[#800000] font-medium transition-colors text-sm"
                data-testid={`nav-${item.key}`}
                onClick={() => scrollToSection(item.href)}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 text-slate-600 hover:text-[#800000]"
                  data-testid="language-toggle"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'HI'}</span>
                </Button> */}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('hi')} data-testid="lang-hi">
                  हिंदी
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Phone CTA - Desktop */}
            <a
              href="tel:+917554055123"
              className="hidden md:flex items-center gap-2 text-teal-700 hover:text-[#800000] font-medium"
              data-testid="phone-cta"
            >
              <Phone className="w-4 h-4 text-[#800000]" />
              <span className="text-sm text-[#800000]">+91-755-4055123</span>
            </a>

            {/* Book Appointment CTA */}
            <Link
              to="/contact"
              className="hidden sm:block"
              data-testid="book-appointment-cta"
              onClick={() => scrollToSection('#contact')}
            >
              <Button className="btn bg-[#800000] text-sm px-5 py-2">
                {t('book_appointment')}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:text-[#800000]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden bg-white border-t border-slate-100 py-4 mobile-menu"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.link}
                  className="px-4 py-2 text-slate-600 hover:text-[#800000] hover:bg-[#800000] rounded-lg font-medium transition-all"
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => scrollToSection(item.href)}
                >
                  {t(item.key)}
                </Link>
              ))}
              <div className="px-4 pt-2">
                <Link
                  to="/contact"
                  className="w-full"
                  data-testid="mobile-book-appointment"
                  onClick={() => scrollToSection('#contact')}
                >
                  <Button className="btn-primary w-full">{t('book_appointment')}</Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;